import {
  useBeforeUnload,
  useLocation,
  useNavigate,
  useSearchParams,
} from '@remix-run/react'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { Map, Marker, Popup, type MapRef, type ViewState } from 'react-map-gl'
import {
  aDrawMode,
  aDrawnFeature,
  aHoverFeature,
  aIsMapEasing,
  aMapCurrentCount,
  aMapDrawRef,
  aMapFilters,
  aMapStyle,
  aMapViewState,
  aMarkLocation,
  aOrganization,
  aPersistedFilters,
  aSelectedListing,
  aUser,
  useAtom,
  useAtomValue,
  type MapFiltersType,
  type SelectedListing,
} from '~/atoms'
import {
  MapView,
  useCurrentMapView,
  useEmbed,
  useHydrated,
  useIsSafari,
  useMapCardSizes,
  useMapEaseTo,
} from '~/hooks'
import {
  CLUSTER_ZOOM_LEVEL,
  DEFAULT_VIEW_STATE,
  MAP_STYLES,
  MAPBOX_ACCESS_TOKEN,
  merge,
  STATUSES,
  TYPES,
} from '~/utils'
import { debounce } from '~/utils/misc'

type ListingMapProps = {
  mapRef: React.RefObject<MapRef>
  children: React.ReactNode
}

function safeParseFloat(float: string | null | undefined): number | undefined {
  if (typeof float !== 'string') return undefined

  const parsed = parseFloat(float)

  return isNaN(parsed) ? undefined : parsed
}

export default function ListingsMap(props: ListingMapProps) {
  const { mapRef, children } = props
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const organization = useAtomValue(aOrganization)
  const user = useAtomValue(aUser)
  const [mapViewState, setMapViewState] = useAtom(aMapViewState)
  const [mapFilters, setMapFilters] = useAtom(aMapFilters)
  const [selectedListing, setSelectedListing] = useAtom(aSelectedListing)
  const currentMapCount = useAtomValue(aMapCurrentCount)
  const mapStyle = useAtomValue(aMapStyle)
  const currentMapView = useCurrentMapView()
  const mapCardSizes = useMapCardSizes()
  const [hoverFeature, setHoverFeature] = useAtom(aHoverFeature)
  const drawMode = useAtomValue(aDrawMode)
  const drawFeature = useAtomValue(aDrawnFeature)
  const drawRef = useAtomValue(aMapDrawRef)
  const [markLocation, setMarkLocation] = useAtom(aMarkLocation)
  const isMapEasing = useAtomValue(aIsMapEasing)
  const mapEaseTo = useMapEaseTo(mapRef)
  const isSafari = useIsSafari()
  const [persistedFilters, setPersistedFilters] = useAtom(aPersistedFilters)
  const hydrated = useHydrated()
  const { isEmbed, openLinksInNewTab } = useEmbed()
  const isTouchMoving = useRef(false)

  const persistFilters = useCallback(
    (map: mapboxgl.Map | undefined, filters: MapFiltersType) => {
      if (!map) return

      const preserve = Boolean(user?.options?.map?.preserve)

      if (!preserve) return

      const center = map.getCenter()
      const zoom = map.getZoom()
      const bearing = map.getBearing()
      const pitch = map.getPitch()

      setPersistedFilters({
        ...filters,
        lng: center.lng.toFixed(6),
        lat: center.lat.toFixed(6),
        z: zoom.toFixed(3),
        b: bearing === 0 ? '0' : bearing.toFixed(3),
        p: pitch === 0 ? '0' : pitch.toFixed(3),
      })
    },
    [user, mapFilters]
  )

  useBeforeUnload(() => {
    const map = mapRef.current?.getMap()
    persistFilters(map, mapFilters)
  })

  useEffect(() => {
    const map = mapRef.current?.getMap()

    return () => {
      persistFilters(map, mapFilters)
    }
  }, [mapRef.current, mapFilters, persistFilters])

  function updateSearchParams() {
    if (currentMapView === MapView.Map) {
      // const url = new URL(window.location.href)
      const center = mapRef.current?.getCenter()
      const zoom = mapRef.current?.getZoom()
      const bearing = mapRef.current?.getBearing()
      const pitch = mapRef.current?.getPitch()

      if (center && zoom) {
        setMapFilters({
          ...mapFilters,
          lng: center!.lng.toFixed(6),
          lat: center!.lat.toFixed(6),
          z: zoom.toFixed(3),
          b: isSafari ? undefined : bearing ? bearing.toFixed(3) : 0,
          p: isSafari ? undefined : pitch ? pitch.toFixed(3) : 0,
        })
      }
    }
  }

  useEffect(() => {
    if (mapRef.current) {
      updateSearchParams()
    }
  }, [currentMapView])

  useEffect(() => {
    if (isSafari && mapRef.current && mapRef.current.getBearing() !== 0) {
      mapRef.current.setBearing(0)
      mapRef.current.setPitch(0)
    }
  }, [isSafari, mapRef])

  useEffect(() => {
    document.documentElement.style.overflowY = 'hidden'
    return () => {
      document.documentElement.style.overflowY = 'auto'
    }
  }, [])

  // useEffect(() => {
  //   // TODO Selected listing
  //   if (!mapRef.current) {
  //     return
  //   }
  //   const zoom = mapRef.current!.getZoom()
  //   if (selectedListing) {
  //     mapRef.current!.easeTo({
  //       center: selectedListing!.location.coordinates as LngLatLike,
  //       zoom: zoom > CLUSTER_ZOOM_LEVEL + 1 ? zoom : CLUSTER_ZOOM_LEVEL + 1,
  //       padding: {
  //         left: mapCardSizes.list + mapCardSizes.single,
  //         top: 0,
  //         bottom: 0,
  //         right: 0,
  //       },
  //       bearing: mapRef.current!.getBearing(),
  //       pitch: mapRef.current!.getPitch(),
  //     })
  //   } else {
  //     mapRef.current!.easeTo({
  //       center: mapRef.current!.getCenter(),
  //       zoom,
  //       padding: { left: mapCardSizes.list, top: 0, bottom: 0, right: 0 },
  //       bearing: mapRef.current!.getBearing(),
  //       pitch: mapRef.current!.getPitch(),
  //     })
  //   }
  // }, [selectedListing])

  // useEffect(() => {
  //   if (selectedListing && currentMapCount === 0) {
  //     mapRef.current!.easeTo({
  //       center: mapRef.current!.getCenter(),
  //       zoom: mapRef.current!.getZoom(),
  //       padding: {
  //         left: mapCardSizes.single,
  //         top: 0,
  //         bottom: 0,
  //         right: 0,
  //       },
  //     })
  //   }
  // }, [currentMapCount])

  const initialViewState: ViewState = useMemo(() => {
    const bearingAndPitch = isSafari
      ? {}
      : {
          pitch: safeParseFloat(searchParams.get('p')),
          bearing: safeParseFloat(searchParams.get('b')),
        }

    const userViewState =
      user?.options?.map?.initialViewState !== undefined
        ? user.options.map.initialViewState
        : {}

    const persisted =
      !!user?.options?.map?.preserve &&
      persistedFilters &&
      Object.keys(persistedFilters).length > 0
        ? {
            latitude: safeParseFloat(persistedFilters.lat),
            longitude: safeParseFloat(persistedFilters.lng),
            zoom: safeParseFloat(persistedFilters.z),
            bearing: safeParseFloat(persistedFilters.b),
            pitch: safeParseFloat(persistedFilters.p),
          }
        : {}

    const params = {
      latitude: safeParseFloat(searchParams.get('lat')),
      longitude: safeParseFloat(searchParams.get('lng')),
      zoom: safeParseFloat(searchParams.get('z')),
    }

    return merge<ViewState>(
      DEFAULT_VIEW_STATE,
      organization!.options.initialViewState,
      userViewState,
      persisted,
      bearingAndPitch,
      params,
      {
        padding: { left: mapCardSizes.list, top: 0, right: 0, bottom: 0 },
      }
    )
  }, [mapCardSizes, searchParams, organization, isSafari])

  const handleMoveEnd = useCallback(
    (
      e: mapboxgl.MapboxEvent<MouseEvent | TouchEvent | WheelEvent | undefined>
    ) => {
      updateSearchParams()
      const { lat, lng } = e.target.getCenter()
      setMapViewState({
        bounds: e.target.getBounds(),
        lat,
        lng,
        zoom: e.target.getZoom(),
      })
      if (!isMapEasing) {
        setMarkLocation(false)
      }
    },
    [updateSearchParams, setMapViewState, isMapEasing, setMarkLocation]
  )

  const debouncedMoveEnd = useMemo(
    () => debounce(handleMoveEnd, 300),
    [handleMoveEnd]
  )

  const onClick = useCallback(
    (e: mapboxgl.MapLayerMouseEvent | mapboxgl.MapLayerTouchEvent) => {
      if (currentMapView !== MapView.Map || !!drawMode) {
        return
      }
      const feature = (e.features || []).find(
        (l) => l.source === 'property-listings'
      )
      if (feature) {
        e.preventDefault()

        // Clear draw selection when clicking on a listing
        if (drawRef && drawFeature) {
          try {
            drawRef.changeMode('simple_select')
            const selectedIds = drawRef.getSelectedIds()
            if (selectedIds.length > 0) {
              drawRef.changeMode('simple_select', {
                featureIds: [],
              })
            }
          } catch (error) {
            console.debug('Could not clear draw selection:', error)
          }
        }

        if (feature.properties!._id) {
          if (e.originalEvent.metaKey || e.originalEvent.ctrlKey) {
            window.open(`/listings/${feature.properties!.slug}`, '_blank')
          } else {
            if (isEmbed && !openLinksInNewTab) {
              navigate(`/${feature.properties!.slug}/dialog${location.search}`)
            } else {
              navigate(`/${feature.properties!.slug}${location.search}`)
            }
            setSelectedListing({
              ...feature.properties,
              location: {
                // @ts-expect-error
                coordinates: feature.geometry.coordinates,
              },
            } as SelectedListing)
          }
        } else {
          mapEaseTo({
            // @ts-expect-error
            center: feature.geometry.coordinates,
            zoom:
              mapViewState!.zoom < 8 && feature.properties?.point_count < 10
                ? CLUSTER_ZOOM_LEVEL - 1
                : CLUSTER_ZOOM_LEVEL + 1,
            padding: {
              left: mapCardSizes.list,
              top: 0,
              bottom: 0,
              right: 0,
            },
            duration: 500,
          })
          setSelectedListing(null)
        }
      } else if (location.pathname !== '/') {
        navigate(`/${location.search}`)
        setSelectedListing(null)
      }
    },
    [
      currentMapView,
      drawMode,
      drawFeature,
      drawRef,
      location,
      mapCardSizes,
      mapViewState,
      isEmbed,
      openLinksInNewTab,
    ]
  )

  if (!organization!.options.initialViewState) {
    return (
      <div>
        <p>Map view state not set</p>
      </div>
    )
  }

  if (!!user?.options?.map?.preserve && !hydrated) return null

  return (
    <div id="map-container" className="relative h-dvh w-dvw">
      <Map
        ref={mapRef}
        initialViewState={initialViewState}
        mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
        mapStyle={MAP_STYLES[mapStyle]}
        antialias={true}
        minZoom={4}
        maxZoom={18}
        style={{ width: '100%', height: '100%', userSelect: 'none' }}
        customAttribution="Associate™ by Resimplifi"
        interactiveLayerIds={['property-clusters', 'property-single-clusters']}
        dragRotate={!isSafari}
        onDblClick={(e) => {
          if (drawMode) {
            e.preventDefault()
          }
        }}
        onMouseMove={(e) => {
          const feature = e.features?.[0]
          if (!mapRef.current) return
          if (
            drawMode === null &&
            feature &&
            (feature.layer.id === 'property-clusters' ||
              feature.layer.id === 'property-single-clusters')
          ) {
            mapRef.current!.getMap().getCanvasContainer().style.cursor =
              'pointer'
          } else {
            mapRef.current!.getMap().getCanvasContainer().style.cursor = 'grab'
          }
        }}
        onMouseDown={() => {
          if (!mapRef.current) return
          mapRef.current!.getMap().getCanvasContainer().style.cursor =
            'grabbing'
        }}
        onMouseUp={() => {
          if (!mapRef.current) return
          mapRef.current!.getMap().getCanvasContainer().style.cursor = 'grab'
        }}
        onLoad={(e) => {
          const { lat, lng } = e.target.getCenter()
          setMapViewState({
            bounds: e.target.getBounds(),
            lat,
            lng,
            zoom: e.target.getZoom(),
          })
        }}
        onMouseEnter={(e) => {
          if (
            drawMode === null &&
            e.features &&
            e.features.length > 0 &&
            // @ts-expect-error
            e.features![0].properties.lat
          ) {
            setHoverFeature(e.features![0].properties as any)
          }
        }}
        onMouseOut={() => {
          setHoverFeature(null)
        }}
        onMouseLeave={() => {
          setHoverFeature(null)
        }}
        onClick={(e) => onClick(e)}
        onTouchMove={() => {
          isTouchMoving.current = true
        }}
        onTouchEnd={(e) => {
          if (isTouchMoving.current) {
            isTouchMoving.current = false
          } else {
            onClick(e)
          }
        }}
        onMoveEnd={debouncedMoveEnd}>
        {markLocation && mapViewState && !isMapEasing && (
          <Marker
            latitude={mapViewState?.lat}
            longitude={mapViewState?.lng}
            color="#7c3aed"
          />
        )}
        {hoverFeature && (
          <Popup
            latitude={hoverFeature.lat}
            longitude={hoverFeature.lng}
            style={{ zIndex: 30 }}>
            <p className="mb-1 line-clamp-1 text-sm font-medium">
              {hoverFeature.name}
            </p>
            {hoverFeature.address && (
              <p className="text-sm">{hoverFeature.address}</p>
            )}
            <p className="text-sm">
              {STATUSES[hoverFeature.status]} - {TYPES[hoverFeature.type]} -{' '}
              {hoverFeature.propertyTypes}
            </p>
            {hoverFeature.isBusinessListing && (
              <p className="text-sm">Business</p>
            )}
          </Popup>
        )}
        {children}
      </Map>
    </div>
  )
}
